import {
  accessoriesOf,
  registrationFee,
  tierForAccessories,
  tierLabel,
  tiersFor,
  toDecimal,
  todayCentral,
  type Job,
  type JobChecklistItem,
  type RegisteredTier,
  type WarrantyManufacturer,
} from "@roof-hawk/core";
import { useEffect, useRef, useState } from "react";

import { TextField, TierOption } from "./warrantyFields";

import { ErrorNote, Modal } from "../../components/ui";
import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { useSession } from "../../session";
import {
  fetchJobWarranty,
  fetchRegisterFacts,
  saveWarranty,
  uploadWarrantyReceipt,
  type RegisterFacts,
  type WarrantyRow,
  type WarrantyValues,
} from "../../lib/warranties";

type Outcome = WarrantyValues["status"];

/** The form as typed: every box a string, so nothing is rounded while typing. */
interface Draft {
  outcome: Outcome;
  manufacturer: WarrantyManufacturer | "";
  tier: RegisteredTier | "";
  accessories: string[];
  warrantyNumber: string;
  orderNumber: string;
  receiptNumber: string;
  shingle: string;
  color: string;
  squares: string;
  fee: string;
  /** Somebody typed the fee, so it no longer follows the tier and squares. */
  feeTyped: boolean;
  installedOn: string;
  registeredOn: string;
  reason: string;
  note: string;
}

const text = (value: string | number | null | undefined): string =>
  value == null ? "" : String(value);

const EMPTY: Draft = {
  outcome: "registered",
  manufacturer: "",
  tier: "",
  accessories: [],
  warrantyNumber: "",
  orderNumber: "",
  receiptNumber: "",
  shingle: "",
  color: "",
  squares: "",
  fee: "",
  feeTyped: false,
  installedOn: "",
  registeredOn: "",
  reason: "",
  note: "",
};

/** The row the job already has, back into the boxes. */
function draftFromRow(row: WarrantyRow): Draft {
  return {
    outcome: row.status,
    manufacturer: text(row.manufacturer) as Draft["manufacturer"],
    tier: text(row.tier) as Draft["tier"],
    accessories: row.accessories ?? [],
    warrantyNumber: text(row.warranty_number),
    orderNumber: text(row.order_number),
    receiptNumber: text(row.receipt_number),
    shingle: text(row.shingle_product),
    color: text(row.shingle_color),
    squares: text(row.squares),
    fee: text(row.fee),
    feeTyped: true,
    installedOn: text(row.installed_on),
    registeredOn: text(row.registered_on) || todayCentral(),
    reason: text(row.ineligible_reason),
    note: text(row.note),
  };
}

/** What the job already knows, for a warranty not yet recorded. */
function draftFromFacts(known: RegisterFacts, stepStatus: string): Draft {
  const tier = text(known.tier) as Draft["tier"];
  const squares = text(known.squares);
  return {
    ...EMPTY,
    outcome: stepStatus === "na" ? "ineligible" : "registered",
    manufacturer: text(known.manufacturer) as Draft["manufacturer"],
    tier,
    accessories: known.advice?.present.map((a) => a.key) ?? [],
    shingle: text(known.shingleProduct),
    color: text(known.shingleColor),
    squares,
    fee: feeFor(tier, squares) ?? "",
    installedOn: text(known.installedOn),
    registeredOn: todayCentral(),
  };
}

/** Why the form cannot be saved as it stands, or null. */
function refusal(form: Draft): string | null {
  if (form.outcome === "registered" && (!form.manufacturer || !form.tier))
    return t("Say whose warranty it is and which one was registered.");
  if (form.outcome === "ineligible" && !form.reason.trim())
    return t("Say why this job could not be registered.");
  if (form.squares.trim() !== "" && !Number.isFinite(Number(form.squares)))
    return t("Squares has to be a number.");
  if (form.fee.trim() !== "" && !Number.isFinite(Number(form.fee)))
    return t("The fee has to be a number.");
  return null;
}

const orNull = (value: string): string | null => value.trim() || null;

/** The boxes, as the row is written. */
function valuesOf(form: Draft): WarrantyValues {
  return {
    status: form.outcome,
    ineligible_reason:
      form.outcome === "ineligible" ? form.reason.trim() : null,
    manufacturer: form.manufacturer || null,
    tier: form.tier || null,
    warranty_number: orNull(form.warrantyNumber),
    order_number: orNull(form.orderNumber),
    receipt_number: orNull(form.receiptNumber),
    shingle_product: orNull(form.shingle),
    shingle_color: orNull(form.color),
    squares: form.squares.trim() === "" ? null : Number(form.squares),
    accessories: form.accessories,
    fee: form.fee.trim() === "" ? 0 : Number(form.fee),
    installed_on: form.installedOn || null,
    registered_on:
      form.outcome === "registered" ? form.registeredOn || null : null,
    note: orNull(form.note),
  };
}

/** The fee the tier and squares work out to, or null when they cannot. */
function feeFor(tier: RegisteredTier | "", squares: string): string | null {
  const fee = registrationFee(tier || null, Number(squares));
  return fee === null ? null : String(toDecimal(fee));
}

/**
 * Registering the manufacturer's warranty on a job (ADR 0309).
 *
 * Opens from the "Register Warranty" step. Most of it is already known: the
 * estimate says whose roof it is and which tier it was priced at, the
 * material order says which of the manufacturer's parts went on, the roof
 * says how many squares, and the checklist says when the work finished. What
 * the person adds is what the manufacturer's receipt says — the warranty
 * number, the fee — and the receipt itself, which lands on the Docs card.
 *
 * Saving writes one row; the database ticks the step, or marks it N/A for a
 * job that could not be registered, so the Warranties page and the checklist
 * can never disagree.
 */
export function RegisterWarrantyModal({
  job,
  item,
  onClose,
  onSaved,
}: {
  job: Job;
  item: JobChecklistItem;
  onClose: () => void;
  /** The row is written; the checklist has moved, so re-read it. */
  onSaved: () => Promise<void>;
}) {
  const { profileId } = useSession();
  const existing = useAsync(() => fetchJobWarranty(job.id), [job.id]);
  const facts = useAsync(() => fetchRegisterFacts(job.id), [job.id]);

  const [form, setForm] = useState<Draft>(EMPTY);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Whether the form has been filled; until then it is not shown, so nobody
  // sees (or saves) the empty form for the moment before it fills.
  const [filled, setFilled] = useState(false);
  // The row this modal already wrote. A save that fails after the insert (the
  // receipt upload, say) leaves it in place, so pressing Save again updates it
  // rather than counting the same warranty twice.
  const savedId = useRef<string | null>(null);

  // Fill the form once, from the row the job has or from what the job knows.
  useEffect(() => {
    if (filled || existing.loading || facts.loading) return;
    setFilled(true);
    if (existing.data) setForm(draftFromRow(existing.data));
    else if (facts.data) setForm(draftFromFacts(facts.data, item.status));
  }, [
    filled,
    existing.loading,
    existing.data,
    facts.loading,
    facts.data,
    item.status,
  ]);

  // The fee follows the tier and the squares until somebody types one. When
  // they no longer work out to a fee (no tier, no rate on file, no squares),
  // the box empties rather than keeping the last tier's amount.
  useEffect(() => {
    if (form.feeTyped) return;
    const fee = feeFor(form.tier, form.squares) ?? "";
    if (fee !== form.fee) setForm((was) => ({ ...was, fee }));
  }, [form.tier, form.squares, form.feeTyped, form.fee]);

  const set = (patch: Partial<Draft>) =>
    setForm((was) => ({ ...was, ...patch }));

  const tiers = form.manufacturer ? tiersFor(form.manufacturer) : [];
  const parts = form.manufacturer ? accessoriesOf(form.manufacturer) : [];
  const qualifies = form.manufacturer
    ? tierForAccessories(form.manufacturer, form.accessories.length)
    : null;

  function toggleAccessory(key: string) {
    set({
      accessories: form.accessories.includes(key)
        ? form.accessories.filter((k) => k !== key)
        : [...form.accessories, key],
    });
  }

  async function save() {
    const why = refusal(form);
    setError(why);
    if (why) return;
    setBusy(true);
    try {
      const id = await saveWarranty({
        jobId: job.id,
        estimateId:
          existing.data?.estimate_id ?? facts.data?.estimateId ?? null,
        existingId: savedId.current ?? existing.data?.id ?? null,
        values: valuesOf(form),
        profileId,
      });
      savedId.current = id;
      if (file) {
        await uploadWarrantyReceipt({
          jobId: job.id,
          warrantyId: id,
          file,
          profileId,
          title: `${tierLabel(form.tier || null)} warranty receipt — ${file.name}`,
        });
      }
      await onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const loading = existing.loading || facts.loading || !filled;

  return (
    <Modal
      title={t("Register Warranty")}
      subtitle={t(
        "What the manufacturer's receipt says. The step ticks itself when this is saved.",
      )}
      onClose={onClose}
      roomy
      actions={
        <>
          <button className="btn-ghost" onClick={onClose} disabled={busy}>
            {t("Cancel")}
          </button>
          <button
            className="btn-primary"
            onClick={() => void save()}
            disabled={busy || loading}
          >
            {busy ? t("Saving…") : t("Save")}
          </button>
        </>
      }
    >
      <ErrorNote error={error ?? existing.error ?? facts.error} />
      {loading ? (
        <div className="stub">{t("Reading the job…")}</div>
      ) : (
        <div className="fgrid one">
          <div className="field">
            <label htmlFor="rw-outcome">{t("Outcome")}</label>
            <select
              id="rw-outcome"
              value={form.outcome}
              onChange={(e) => set({ outcome: e.target.value as Outcome })}
            >
              <option value="registered">{t("Registered")}</option>
              <option value="pending">{t("Not yet — still to file")}</option>
              <option value="ineligible">
                {t("Could not be registered — mark N/A")}
              </option>
            </select>
          </div>

          {form.outcome === "ineligible" && (
            <div className="field">
              <label htmlFor="rw-reason">{t("Why not")}</label>
              <input
                id="rw-reason"
                placeholder={t(
                  "Customer provided the materials, only 1 of 4 TAMKO parts…",
                )}
                value={form.reason}
                onChange={(e) => set({ reason: e.target.value })}
              />
            </div>
          )}

          <div className="field">
            <label htmlFor="rw-manufacturer">{t("Manufacturer")}</label>
            <select
              id="rw-manufacturer"
              value={form.manufacturer}
              onChange={(e) =>
                set({
                  manufacturer: e.target.value as Draft["manufacturer"],
                  tier: "",
                  accessories: [],
                })
              }
            >
              <option value="">—</option>
              <option value="TAMKO">TAMKO</option>
              <option value="GAF">GAF</option>
            </select>
          </div>

          {form.manufacturer && (
            <div className="field">
              <label>
                {t("{manufacturer} parts on the roof", {
                  manufacturer: form.manufacturer,
                })}
              </label>
              <div className="na-hint" style={{ marginBottom: 4 }}>
                {t("{n} of {total}", {
                  n: form.accessories.length,
                  total: parts.length,
                })}
                {qualifies
                  ? ` — ${t("qualifies for {tier}", { tier: tierLabel(qualifies) })}`
                  : ` — ${t("not enough for an enhanced warranty")}`}
              </div>
              {parts.map((part) => (
                <label
                  key={part.key}
                  style={{ display: "block", fontWeight: 400 }}
                >
                  <input
                    type="checkbox"
                    checked={form.accessories.includes(part.key)}
                    onChange={() => toggleAccessory(part.key)}
                  />{" "}
                  {part.name}
                </label>
              ))}
            </div>
          )}

          <div className="field">
            <label htmlFor="rw-tier">{t("Warranty registered")}</label>
            <select
              id="rw-tier"
              value={form.tier}
              onChange={(e) =>
                set({ tier: e.target.value as Draft["tier"], feeTyped: false })
              }
              disabled={!form.manufacturer}
            >
              <option value="">—</option>
              {tiers.map((t) => (
                <TierOption key={t} tier={t} of={parts.length} />
              ))}
            </select>
          </div>

          <div className="fgrid">
            <TextField
              id="rw-number"
              label={t("Warranty #")}
              value={form.warrantyNumber}
              onChange={(warrantyNumber) => set({ warrantyNumber })}
            />
            <TextField
              id="rw-order"
              label={t("Order #")}
              value={form.orderNumber}
              onChange={(orderNumber) => set({ orderNumber })}
            />
            <TextField
              id="rw-receipt-number"
              label={t("Receipt #")}
              value={form.receiptNumber}
              onChange={(receiptNumber) => set({ receiptNumber })}
            />
            <TextField
              id="rw-squares"
              label={t("Squares registered")}
              value={form.squares}
              onChange={(squares) => set({ squares })}
              decimal
            />
            <TextField
              id="rw-shingle"
              label={t("Shingle")}
              value={form.shingle}
              onChange={(shingle) => set({ shingle })}
            />
            <TextField
              id="rw-color"
              label={t("Colour")}
              value={form.color}
              onChange={(color) => set({ color })}
            />
            <TextField
              id="rw-fee"
              label={t("Fee paid")}
              value={form.fee}
              onChange={(fee) => set({ fee, feeTyped: true })}
              decimal
            />
            <TextField
              id="rw-installed"
              label={t("Installed on")}
              value={form.installedOn}
              onChange={(installedOn) => set({ installedOn })}
              date
            />
            {form.outcome === "registered" && (
              <TextField
                id="rw-registered"
                label={t("Registered on")}
                value={form.registeredOn}
                onChange={(registeredOn) => set({ registeredOn })}
                date
              />
            )}
          </div>

          <div className="field">
            <label htmlFor="rw-file">{t("Receipt")}</label>
            <input
              id="rw-file"
              type="file"
              accept="application/pdf,.pdf,image/*"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <div className="na-hint">
              {t(
                "Goes on the job's Docs card and opens from the Warranties page.",
              )}
            </div>
          </div>

          <div className="field">
            <label htmlFor="rw-note">{t("Note")}</label>
            <textarea
              id="rw-note"
              rows={2}
              value={form.note}
              onChange={(e) => set({ note: e.target.value })}
            />
          </div>
        </div>
      )}
    </Modal>
  );
}
